import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { fetchData } from "../../../api/client";
import { isWhatsappTemplateUsable } from "../../../utils/whatsappTemplateLifecycle";
import { uploadOne } from "../../../hooks/useS3Upload";
import { BR_UF_OPTIONS, estimateSmsSegments, labelChannel, channelRequiresSenderIdentity, formatChannelIdentity } from "../campaignUi";
import DynamicFields from "../../../components/DynamicFields";
import { useAutomationCatalog } from "../../../hooks/useAutomationRules";
import { getCampaignContextVariables } from "../../../utils/chatbotContextVariables";
import VoiceCampaignMessagePanel, {
  EMPTY_VOICE_FORM,
  validateVoiceForm,
  buildVoiceContentPayload,
} from "./VoiceCampaignMessagePanel";

const CHANNEL_VISUAL = {
  whatsapp: { Icon: ChatBubbleOutlineIcon, color: "#128C7E", bg: "rgba(18,140,126,0.12)" },
  email: { Icon: EmailOutlinedIcon, color: "#1565C0", bg: "rgba(21,101,192,0.12)" },
  sms: { Icon: SmsOutlinedIcon, color: "#6A1B9A", bg: "rgba(106,27,154,0.12)" },
  voice: { Icon: PhoneOutlinedIcon, color: "#00838F", bg: "rgba(0,131,143,0.12)" },
};

const EMPTY_FORM = {
  name: "",
  tag: "",
  subject: "",
  text: "",
  html: "",
  templateId: "",
  templateParams: [],
  templateMedia: {},
  templateLocation: { latitude: "", longitude: "", name: "", address: "" },
  idsGrupo: [],
  tags: [],
  uf: "",
  cidade: "",
  scheduleAt: "",
  throttlePerMinute: 30,
  budget: "",
};

/** Remove origem do schema — o canal do disparo já define id_origem. */
function filterLeadDefaultsSchema(form) {
  if (!form || typeof form !== "object") return null;
  const fields =
    form.acoes && typeof form.acoes === "object"
      ? form.acoes
      : form.fields && typeof form.fields === "object"
        ? form.fields
        : form;
  if (!fields || typeof fields !== "object") return form;
  const next = { ...fields };
  delete next.mudarorigem;
  delete next.mudarorigemdeleads;
  if (form.acoes) return { ...form, acoes: next };
  if (form.fields) return { ...form, fields: next };
  return next;
}

function leadDefaultsHaveValues(values) {
  if (!values || typeof values !== "object") return false;
  return Object.entries(values).some(([key, value]) => {
    if (key === "somenteusuarioslogados") return false;
    if (value == null || value === "" || value === "default") return false;
    if (Array.isArray(value) && !value.length) return false;
    const s = String(value).trim().toLowerCase();
    if (s === "nao alterar" || s === "não alterar") return false;
    return true;
  });
}

function templateScore(template) {
  return (template?.idGupshup ? 1_000_000 : 0) + Number(template?.id || 0);
}

/** Remove IDs repetidos e colapsa o mesmo elementName+idioma (sync duplicado). */
function dedupeChannelTemplates(items = []) {
  const byId = new Map();
  for (const item of items) {
    const id = Number(item?.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    if (!byId.has(id)) byId.set(id, item);
  }
  const bestByKey = new Map();
  for (const item of byId.values()) {
    const key = [
      String(item.elementName || item.name || "")
        .trim()
        .toLowerCase(),
      String(item.languageCode || "")
        .trim()
        .toLowerCase(),
    ].join("::");
    const prev = bestByKey.get(key);
    if (!prev || templateScore(item) > templateScore(prev)) {
      bestByKey.set(key, item);
    }
  }
  return [...bestByKey.values()].sort((a, b) =>
    String(a.elementName || a.name || "").localeCompare(
      String(b.elementName || b.name || ""),
      "pt-BR",
      { sensitivity: "base" }
    )
  );
}

function templateOptionLabel(template) {
  if (!template) return "";
  const name = template.elementName || template.name || `Template #${template.id}`;
  const bits = [name];
  if (template.languageCode) bits.push(String(template.languageCode));
  if (template.category) bits.push(String(template.category));
  return bits.join(" · ");
}

function templateSearchText(template) {
  return [
    template?.elementName,
    template?.name,
    template?.preview,
    template?.bodyText,
    template?.languageCode,
    template?.category,
    template?.templateType,
    template?.id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

const MEDIA_KIND_META = {
  image: {
    label: "Imagem do cabeçalho",
    accept: "image/jpeg,image/png",
    missing: "a imagem do cabeçalho",
  },
  video: {
    label: "Vídeo do cabeçalho",
    accept: "video/mp4,video/3gpp",
    missing: "o vídeo do cabeçalho",
  },
  document: {
    label: "Arquivo do cabeçalho",
    accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf",
    missing: "o arquivo do cabeçalho",
  },
};

function templateRequirementsOf(template) {
  const empty = { headerKind: "none", bodyParamCount: 0, locationRequired: false, media: [] };
  if (!template) return empty;
  const fromApi =
    template.requirements && typeof template.requirements === "object"
      ? template.requirements
      : null;
  const headerKind = String(fromApi?.headerKind || template.headerKind || "none").toLowerCase();
  const bodyParamCount = Math.max(
    0,
    Number(fromApi?.bodyParamCount ?? template.bodyParamCount) || 0
  );
  const media = Array.isArray(fromApi?.media)
    ? fromApi.media
    : MEDIA_KIND_META[headerKind]
      ? [
          {
            key: "header",
            kind: headerKind,
            label: MEDIA_KIND_META[headerKind].label,
            accept: MEDIA_KIND_META[headerKind].accept,
          },
        ]
      : [];
  return {
    headerKind,
    bodyParamCount,
    locationRequired: Boolean(fromApi?.locationRequired) || headerKind === "location",
    media,
  };
}

function parseTemplateParamValues(raw, count) {
  const parts = Array.isArray(raw)
    ? raw.map((part) => String(part ?? ""))
    : String(raw || "")
        .split(",")
        .map((part) => part.trim());
  const n = Math.max(0, Number(count) || 0);
  if (n <= 0) return parts.map((part) => String(part || "").trim()).filter(Boolean);
  return Array.from({ length: n }, (_, index) => parts[index] || "");
}

function templateCompletionIssues(template, form) {
  if (!template) return [];
  const requirements = templateRequirementsOf(template);
  const issues = [];
  const values = parseTemplateParamValues(form?.templateParams, requirements.bodyParamCount);
  values.forEach((value, index) => {
    if (!String(value || "").trim()) issues.push(`campo {{${index + 1}}}`);
  });
  for (const slot of requirements.media) {
    const attached = form?.templateMedia?.[slot.key];
    if (!String(attached?.url || "").trim()) {
      const fallback = MEDIA_KIND_META[slot.kind]?.missing;
      issues.push(slot.label ? slot.label.toLowerCase() : fallback || "arquivo do template");
    }
  }
  if (requirements.locationRequired) {
    const lat = Number(form?.templateLocation?.latitude);
    const lng = Number(form?.templateLocation?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      issues.push("latitude e longitude");
    }
  }
  return issues;
}

function previewTemplateBody(bodyText, params = []) {
  const text = String(bodyText || "").trim();
  if (!text) return "";
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const idx = Number(n) - 1;
    const value = params[idx];
    if (value != null && String(value).trim() !== "") return String(value);
    return `{{${n}}}`;
  });
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function plainTextToEmailHtml(text) {
  const escaped = escapeHtml(text).trim();
  if (!escaped) {
    return '<p style="margin:0;color:#888;">Nenhum conteúdo para pré-visualizar.</p>';
  }
  return escaped
    .split(/\n{2,}/)
    .map(
      (paragraph) =>
        `<p style="margin:0 0 12px;">${paragraph.replace(/\n/g, "<br>")}</p>`
    )
    .join("");
}

function buildEmailPreviewDocument({ html, text, subject }) {
  const body = String(html || "").trim() || plainTextToEmailHtml(text);
  const title = escapeHtml(subject || "Pré-visualização do e-mail");
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  <style>
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      max-width: 100%;
      background: #fff;
      color: #202124;
      font-family: Roboto, Arial, Helvetica, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      word-break: break-word;
      overflow-x: hidden;
    }
    body { padding: 0; box-sizing: border-box; }
    *, *::before, *::after { box-sizing: border-box; }
    table, td, th, div, section, center, p, h1, h2, h3, h4, h5, h6, span, a, img {
      max-width: 100% !important;
    }
    table {
      width: 100% !important;
      border-collapse: collapse !important;
    }
    img {
      height: auto !important;
      display: block;
    }
    a {
      color: #1a73e8;
    }
  </style>
</head>
<body>${body}</body>
</html>`;
}

function EmailPreviewFrame({ subject, srcDoc, variant }) {
  const isMobile = variant === "mobile";
  const frameWidth = isMobile ? 360 : "100%";
  const iframeHeight = isMobile ? 520 : 480;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {isMobile ? (
          <SmartphoneOutlinedIcon fontSize="small" color="action" />
        ) : (
          <ComputerOutlinedIcon fontSize="small" color="action" />
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {isMobile ? "Smartphone" : "Computador"}
        </Typography>
      </Stack>

      <Box
        sx={{
          mx: isMobile ? "auto" : 0,
          width: isMobile ? 376 : "100%",
          maxWidth: "100%",
          borderRadius: isMobile ? "28px" : 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: isMobile ? "#111" : "#f6f8fc",
          p: isMobile ? 1.25 : 0,
          boxShadow: isMobile ? "0 12px 28px rgba(0,0,0,0.18)" : "none",
        }}
      >
        {isMobile ? (
          <Box
            sx={{
              width: 96,
              height: 10,
              borderRadius: 999,
              bgcolor: "#2a2a2a",
              mx: "auto",
              mb: 1,
            }}
          />
        ) : null}

        <Box
          sx={{
            borderRadius: isMobile ? "20px" : 2,
            overflow: "hidden",
            bgcolor: "#fff",
            border: isMobile ? "none" : "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "#fff",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, lineHeight: 1.35, wordBreak: "break-word" }}
            >
              {subject.trim() || "(Sem assunto)"}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              De: Sua empresa
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Para: cliente@email.com
            </Typography>
          </Box>

          <Box
            sx={{
              width: frameWidth,
              maxWidth: "100%",
              mx: "auto",
              bgcolor: "#fff",
            }}
          >
            <Box
              component="iframe"
              title={isMobile ? "Pré-visualização no smartphone" : "Pré-visualização no computador"}
              sandbox=""
              srcDoc={srcDoc}
              sx={{
                display: "block",
                width: "100%",
                height: iframeHeight,
                border: 0,
                bgcolor: "#fff",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function EmailClientPreviewDialog({ open, onClose, subject, srcDoc, usingPlainTextFallback }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        Como o cliente vê
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
          Simulação da leitura em um cliente de e-mail na web, no computador e no smartphone.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {usingPlainTextFallback ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Sem HTML cadastrado: a pré-visualização usa o texto simples.
          </Alert>
        ) : null}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2.5}
          alignItems={{ xs: "stretch", md: "flex-start" }}
        >
          <Box sx={{ flex: 1.35, minWidth: 0 }}>
            <EmailPreviewFrame variant="desktop" subject={subject} srcDoc={srcDoc} />
          </Box>
          <Box sx={{ flex: 0.9, minWidth: 0, maxWidth: { md: 400 } }}>
            <EmailPreviewFrame variant="mobile" subject={subject} srcDoc={srcDoc} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

function defaultBroadcastName(channelKey) {
  const map = {
    whatsapp: "Disparo WhatsApp",
    email: "Disparo de e-mail",
    sms: "Disparo SMS",
    voice: "Disparo Telefone Xpro",
  };
  return map[channelKey] || "Novo disparo";
}

function Panel({ step, title, subtitle, children, sx }) {
  return (
    <Box
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        bgcolor: "background.paper",
        p: { xs: 2, md: 2.75 },
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        ...sx,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
        {step != null ? (
          <Box
            aria-hidden
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              typography: "caption",
              fontWeight: 800,
              flexShrink: 0,
              mt: 0.15,
            }}
          >
            {step}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function ChannelCard({ selected, name, channelKey, identity, onClick, compact = false }) {
  const visual = CHANNEL_VISUAL[String(channelKey || "").toLowerCase()] || {
    Icon: ChatBubbleOutlineIcon,
    color: "#546E7A",
    bg: "rgba(84,110,122,0.12)",
  };
  const Icon = visual.Icon;
  const identityText = String(identity || "").trim();
  const needsSender = channelRequiresSenderIdentity(channelKey);
  const subtitle = identityText
    ? formatChannelIdentity(channelKey, identityText)
    : needsSender
      ? `${labelChannel(channelKey) || channelKey} · Remetente não configurado`
      : labelChannel(channelKey) || channelKey;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: "unset",
        cursor: "pointer",
        display: "block",
        width: "100%",
        boxSizing: "border-box",
        borderRadius: 3,
        border: "1.5px solid",
        borderColor: selected ? visual.color : "divider",
        bgcolor: selected ? visual.bg : "background.paper",
        px: compact ? 1.75 : 2.25,
        py: compact ? 1.5 : 2.25,
        height: "100%",
        transition: "border-color 140ms ease, background-color 140ms ease, transform 140ms ease",
        "&:hover": {
          borderColor: selected ? visual.color : "text.disabled",
          bgcolor: selected ? visual.bg : "action.hover",
          transform: compact ? "none" : "translateY(-1px)",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: compact ? 36 : 44,
            height: compact ? 36 : 44,
            borderRadius: 2,
            bgcolor: selected ? "background.paper" : visual.bg,
            color: visual.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon fontSize={compact ? "small" : "medium"} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant={compact ? "subtitle2" : "subtitle1"}
            sx={{ fontWeight: 700, lineHeight: 1.25 }}
            noWrap
          >
            {name}
          </Typography>
          <Typography
            variant="caption"
            color={needsSender && !identityText ? "warning.main" : "text.secondary"}
            sx={{ display: "block", mt: 0.25, lineHeight: 1.35 }}
            noWrap
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function AdvancedPanel({ title, summary, defaultExpanded = false, children }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={defaultExpanded}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px !important",
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 48 }}>
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {summary ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.15 }}>
              {summary}
            </Typography>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

function AiFieldAdornment({ loading, label, onClick }) {
  return (
    <InputAdornment
      position="end"
      sx={{
        position: "absolute",
        top: 6,
        right: 6,
        m: 0,
        height: "auto",
        maxHeight: "none",
      }}
    >
      <Tooltip title={label}>
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={loading}
            onClick={onClick}
            aria-label={label}
            sx={{ p: 0.5 }}
          >
            {loading ? <CircularProgress size={16} /> : <AutoAwesomeIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </InputAdornment>
  );
}

function aiFieldInputProps({ loading, label, onClick }) {
  return {
    sx: { position: "relative", pr: 4.5 },
    endAdornment: (
      <AiFieldAdornment loading={loading} label={label} onClick={onClick} />
    ),
  };
}

const CAMPAIGN_VAR_SHORT_LABEL = {
  $nomecliente: "Nome",
  $nomeclientevalido: "Nome válido",
  $cadastroencontrado: "Cadastro encontrado",
  $lgpd: "LGPD aceita",
  $cpfcliente: "CPF",
  $cnpjcliente: "CNPJ",
  $emailcliente: "E-mail",
  $telefonecliente: "Telefone",
  $celularcliente: "Celular",
  $tagscliente: "Tags",
  $etapaconsciencia: "Nível de consciência",
};

function CampaignContextVariableChips({ onInsert }) {
  const variables = useMemo(() => getCampaignContextVariables(), []);
  if (!variables.length) return null;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {variables.map((v) => {
        const short = CAMPAIGN_VAR_SHORT_LABEL[v.name] || v.label;
        return (
          <Tooltip key={v.name} title={`${v.name} — ${v.label}`} enterDelay={300}>
            <Chip
              size="small"
              variant="outlined"
              label={short}
              onClick={() => onInsert?.(v.name)}
              sx={{
                cursor: "pointer",
                bgcolor: "background.paper",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: "background.paper",
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}

function CadastroPersonalizePanel({ onInsert, fieldHint = "no campo acima" }) {
  return (
    <AdvancedPanel
      title="Personalizar com dados do cadastro"
      summary="Nome, e-mail, telefone e outros — opcional"
    >
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary">
          Clique para inserir {fieldHint}. No envio, cada pessoa recebe o valor do próprio cadastro.
        </Typography>
        <CampaignContextVariableChips onInsert={onInsert} />
      </Stack>
    </AdvancedPanel>
  );
}

export default function OmnichannelCampaignCreateDialog({
  open,
  onClose,
  onCreated,
  initialClientIds = [],
}) {
  const [capabilities, setCapabilities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [originId, setOriginId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [voiceFields, setVoiceFields] = useState(EMPTY_VOICE_FORM);
  const [nameTouched, setNameTouched] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [advancedKey, setAdvancedKey] = useState(0);
  const [aiBusy, setAiBusy] = useState("");
  const [aiPrompt, setAiPrompt] = useState({ open: false, target: "text", value: "" });
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [leadDefaultsValues, setLeadDefaultsValues] = useState({});
  const [leadDefaultsLabels, setLeadDefaultsLabels] = useState({});
  const [focusedTemplateParam, setFocusedTemplateParam] = useState(0);
  const [mediaBusyKey, setMediaBusyKey] = useState("");
  const [focusedEmailField, setFocusedEmailField] = useState("text");

  const { data: automationMeta } = useAutomationCatalog(1);
  const leadDefaultsSchema = useMemo(() => {
    const acoes = automationMeta?.data?.acoes ?? automationMeta?.acoes ?? [];
    const def = acoes.find((a) => Number(a.id) === 4);
    return filterLeadDefaultsSchema(def?.form || null);
  }, [automationMeta]);
  const leadDefaultsSummary = useMemo(() => {
    if (!leadDefaultsHaveValues(leadDefaultsValues)) return "Padrão do canal (só em solicitação nova)";
    const labels = Object.values(leadDefaultsLabels || {}).filter(
      (v) => v != null && String(v).trim() !== "" && String(v).toLowerCase() !== "default"
    );
    if (labels.length) {
      return labels.slice(0, 4).join(" · ") + (labels.length > 4 ? "…" : "");
    }
    return "Personalizado para solicitações novas";
  }, [leadDefaultsValues, leadDefaultsLabels]);

  const selected = useMemo(
    () => capabilities.find((item) => String(item.originId) === String(originId)) || null,
    [capabilities, originId]
  );

  const smsMeta = useMemo(
    () => (selected?.channelKey === "sms" ? estimateSmsSegments(form.text) : null),
    [selected?.channelKey, form.text]
  );

  const emailHtmlPreviewDoc = useMemo(
    () =>
      buildEmailPreviewDocument({
        html: form.html,
        text: form.text,
        subject: form.subject,
      }),
    [form.html, form.text, form.subject]
  );

  const isAllAudience =
    !(Array.isArray(form.idsGrupo) && form.idsGrupo.length) &&
    !(Array.isArray(form.tags) && form.tags.length) &&
    !form.uf &&
    !String(form.cidade || "").trim();

  const selectedTemplate = useMemo(
    () => templates.find((item) => String(item.id) === String(form.templateId)) || null,
    [templates, form.templateId]
  );
  const templateRequirements = useMemo(
    () => templateRequirementsOf(selectedTemplate),
    [selectedTemplate]
  );
  const templateParamCount = templateRequirements.bodyParamCount;
  const templateParamValues = useMemo(
    () => parseTemplateParamValues(form.templateParams, templateParamCount),
    [form.templateParams, templateParamCount]
  );
  const templateIssues = useMemo(
    () => (selectedTemplate ? templateCompletionIssues(selectedTemplate, form) : []),
    [selectedTemplate, form]
  );
  const templateBodyPreview = useMemo(
    () =>
      previewTemplateBody(
        selectedTemplate?.bodyText || selectedTemplate?.preview || "",
        templateParamValues
      ),
    [selectedTemplate, templateParamValues]
  );
  const fixedClientIds = useMemo(
    () =>
      [
        ...new Set(
          (Array.isArray(initialClientIds) ? initialClientIds : [])
            .map(Number)
            .filter((id) => Number.isFinite(id) && id > 0)
        ),
      ],
    [initialClientIds]
  );
  const hasFixedAudience = fixedClientIds.length > 0;

  const hasExtraAudienceFilters =
    (Array.isArray(form.tags) && form.tags.length > 0) ||
    Boolean(form.uf) ||
    Boolean(String(form.cidade || "").trim());

  useEffect(() => {
    if (!open) return;
    setError("");
    setPreview(null);
    setConfirmAll(false);
    setNameTouched(false);
    setForm(EMPTY_FORM);
    setVoiceFields(EMPTY_VOICE_FORM);
    setOriginId("");
    setTemplates([]);
    setTagOptions([]);
    setAiBusy("");
    setAiPrompt({ open: false, target: "text", value: "" });
    setEmailPreviewOpen(false);
    setLeadDefaultsValues({});
    setLeadDefaultsLabels({});
    setFocusedEmailField("text");
    setFocusedTemplateParam(0);
    setAdvancedKey((key) => key + 1);
    Promise.all([
      fetchData("/campaigns/capabilities", "GET"),
      fetchData("/clients-groups?limit=200", "GET").catch(() => ({ data: [] })),
      fetchData("/client-tags?limit=200&includeUsed=1", "GET").catch(() => ({ data: [] })),
    ])
      .then(([capabilityResponse, groupsResponse, tagsResponse]) => {
        const items = (capabilityResponse?.data || []).filter(
          (item) => item.coldOutbound !== false
        );
        setCapabilities(items);
        setGroups(groupsResponse?.data?.items || groupsResponse?.data || []);
        const tagsRaw = tagsResponse?.data ?? tagsResponse;
        const names = [
          ...new Set(
            (Array.isArray(tagsRaw) ? tagsRaw : [])
              .map((tag) => String(tag?.nome ?? tag?.name ?? "").trim())
              .filter(Boolean)
          ),
        ].sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }));
        setTagOptions(names);
        const first = items[0] || null;
        if (first) {
          setOriginId(String(first.originId));
          setForm((current) => ({
            ...current,
            name: defaultBroadcastName(first.channelKey),
            throttlePerMinute: 30,
          }));
        }
      })
      .catch((requestError) =>
        setError(requestError?.message || "Não foi possível carregar canais e grupos.")
      );
  }, [open]);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      templateId: "",
      templateParams: [],
      templateMedia: {},
      templateLocation: { latitude: "", longitude: "", name: "", address: "" },
    }));
    setFocusedTemplateParam(0);
    setMediaBusyKey("");
    setTemplates([]);
    setPreview(null);
    if (!originId || !selected?.templates) {
      setTemplatesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setTemplatesLoading(true);
    fetchData(`/channels/${originId}/templates`, "GET")
      .then((response) => {
        if (cancelled) return;
        const items = response?.data?.templates || response?.data || [];
        const approved = (Array.isArray(items) ? items : []).filter(isWhatsappTemplateUsable);
        setTemplates(dedupeChannelTemplates(approved));
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [originId, selected?.templates]);

  const change = (key) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
    if (["uf", "cidade"].includes(key)) {
      setPreview(null);
      setConfirmAll(false);
    }
  };

  const setTemplateParamAt = (index, value) => {
    setForm((current) => {
      const count = Math.max(templateParamCount, index + 1);
      const next = parseTemplateParamValues(current.templateParams, count);
      next[index] = value;
      return { ...current, templateParams: next };
    });
  };

  const uploadTemplateMedia = async (slot, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !slot?.key) return;
    const kind = String(slot.kind || "").toLowerCase();
    const mime = String(file.type || "").toLowerCase();
    const name = String(file.name || "").toLowerCase();
    const accepted =
      kind === "image"
        ? mime === "image/jpeg" || mime === "image/png" || /\.(jpe?g|png)$/.test(name)
        : kind === "video"
          ? mime === "video/mp4" || mime === "video/3gpp" || /\.(mp4|3gp)$/.test(name)
          : /\.(pdf|doc|docx|xls|xlsx|ppt|pptx)$/.test(name) || mime === "application/pdf";
    if (!accepted) {
      setError(`Escolha um arquivo compatível com ${slot.label || "o template"}.`);
      return;
    }
    setMediaBusyKey(slot.key);
    setError("");
    try {
      const uploaded = await uploadOne(file, { prefix: "campaigns/whatsapp" });
      setForm((current) => ({
        ...current,
        templateMedia: {
          ...(current.templateMedia || {}),
          [slot.key]: { url: uploaded.url, filename: file.name, kind },
        },
      }));
    } catch (requestError) {
      setError(requestError?.message || "Não foi possível enviar o arquivo.");
    } finally {
      setMediaBusyKey("");
    }
  };

  const setAudienceGroups = (_event, value) => {
    const next = Array.isArray(value)
      ? value
          .map((item) => Number(item?.id ?? item))
          .filter((id) => Number.isFinite(id) && id > 0)
      : [];
    setForm((current) => ({ ...current, idsGrupo: next }));
    setPreview(null);
    setConfirmAll(false);
  };

  const setAudienceTags = (_event, value) => {
    const next = Array.isArray(value)
      ? value.map((item) => String(item || "").trim()).filter(Boolean)
      : [];
    setForm((current) => ({ ...current, tags: next }));
    setPreview(null);
    setConfirmAll(false);
  };

  const generateWithAi = async (target, instruction = "") => {
    const mode = target === "html" ? "html" : "text";
    const sourceText = String(form.text || "").trim();
    setAiBusy(target);
    setError("");
    try {
      const response = await fetchData("/campaigns/ai-copy", "POST", {
        mode,
        channelKey: selected?.channelKey || "whatsapp",
        text: sourceText || undefined,
        instruction: sourceText ? undefined : String(instruction || "").trim(),
      });
      const content = String(response?.data?.content || "").trim();
      if (!content) throw new Error("A IA não retornou um conteúdo válido.");
      setForm((current) => ({ ...current, [target]: content }));
      setAiPrompt({ open: false, target: "text", value: "" });
    } catch (requestError) {
      setError(requestError?.message || "Não foi possível gerar o conteúdo com IA.");
    } finally {
      setAiBusy("");
    }
  };

  const requestAiCopy = (target) => {
    const hasSourceText = Boolean(String(form.text || "").trim());
    if (hasSourceText) {
      generateWithAi(target);
      return;
    }
    setAiPrompt({ open: true, target, value: "" });
  };

  const selectChannel = (item) => {
    setOriginId(String(item.originId));
    setPreview(null);
    setConfirmAll(false);
    setEmailPreviewOpen(false);
    setVoiceFields(EMPTY_VOICE_FORM);
    if (!nameTouched) {
      setForm((current) => ({
        ...current,
        name: defaultBroadcastName(item.channelKey),
        subject: "",
        text: "",
        html: "",
        templateId: "",
        templateParams: [],
        templateMedia: {},
        templateLocation: { latitude: "", longitude: "", name: "", address: "" },
      }));
    }
  };

  const audience = () =>
    hasFixedAudience
      ? { client_ids: fixedClientIds }
      : {
          ...(Array.isArray(form.idsGrupo) && form.idsGrupo.length
            ? { ids_grupo: form.idsGrupo.map(Number) }
            : {}),
          ...(Array.isArray(form.tags) && form.tags.length ? { tags: form.tags } : {}),
          ...(form.uf ? { uf: String(form.uf).toUpperCase() } : {}),
          ...(String(form.cidade || "").trim()
            ? { cidade: String(form.cidade).trim() }
            : {}),
        };

  const previewAudience = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await fetchData("/campaigns/broadcasts/preview-audience", "POST", {
        originId: Number(originId),
        audience: audience(),
      });
      setPreview(response?.data || null);
    } catch (requestError) {
      setError(requestError?.message || "Não foi possível estimar a audiência.");
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!hasFixedAudience && isAllAudience && !confirmAll) {
      setError("Para enviar a toda a base, confirme o aviso abaixo ou filtre por nível de consciência.");
      return;
    }
    if (selected?.channelKey === "voice") {
      const voiceErr = validateVoiceForm(form, voiceFields);
      if (voiceErr) {
        setError(voiceErr);
        return;
      }
    }
    const templateIssuesNow = selected?.templates
      ? templateCompletionIssues(selectedTemplate, form)
      : [];
    if (templateIssuesNow.length) {
      setError(
        `Complete o template antes de criar o envio: ${templateIssuesNow.join(", ")}.`
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      const params = parseTemplateParamValues(form.templateParams, templateParamCount).map((item) =>
        String(item || "").trim()
      );
      const templateMedia = templateRequirements.media.map((slot) => {
        const attached = form.templateMedia?.[slot.key] || {};
        return {
          key: slot.key,
          type: slot.kind,
          link: attached.url,
          filename: attached.filename || undefined,
        };
      });
      const templateHeader = templateRequirements.locationRequired
        ? {
            type: "location",
            latitude: Number(form.templateLocation?.latitude),
            longitude: Number(form.templateLocation?.longitude),
            name: String(form.templateLocation?.name || "").trim() || undefined,
            address: String(form.templateLocation?.address || "").trim() || undefined,
          }
        : templateMedia.length === 1 && templateMedia[0].key === "header"
          ? {
              type: templateMedia[0].type,
              link: templateMedia[0].link,
              filename: templateMedia[0].filename,
            }
          : undefined;
      const voicePayload =
        selected?.channelKey === "voice" ? buildVoiceContentPayload(form, voiceFields) : null;
      const response = await fetchData("/campaigns/broadcasts", "POST", {
        name: form.name.trim(),
        originId: Number(originId),
        tag: form.tag.trim() || undefined,
        audience: audience(),
        scheduleAt: form.scheduleAt || undefined,
        throttlePerMinute: Number(form.throttlePerMinute) || 30,
        budget: Number(form.budget) || 0,
        ...(leadDefaultsHaveValues(leadDefaultsValues)
          ? { leadDefaults: leadDefaultsValues }
          : {}),
        content: {
          templateId: Number(form.templateId) || undefined,
          templateParams: params.length ? params : undefined,
          ...(templateHeader ? { templateHeader } : {}),
          ...(templateMedia.length ? { templateMedia } : {}),
          subject: form.subject.trim() || undefined,
          text: voicePayload ? voicePayload.text || form.text : form.text,
          html: form.html.trim() || undefined,
          ...(voicePayload?.voice ? { voice: voicePayload.voice } : {}),
        },
      });
      onCreated?.(response?.data?.id);
    } catch (requestError) {
      setError(requestError?.message || "Não foi possível criar o disparo.");
    } finally {
      setBusy(false);
    }
  };

  const isVoice = selected?.channelKey === "voice";
  const needsContent = selected && !selected.templates && !isVoice;
  const emailOk = selected?.channelKey !== "email" || Boolean(form.subject.trim());
  const voiceOk = !isVoice || !validateVoiceForm(form, voiceFields);
  const senderOk =
    !selected ||
    !channelRequiresSenderIdentity(selected.channelKey) ||
    Boolean(String(selected.identity || "").trim());
  const audienceOk = hasFixedAudience || !isAllAudience || confirmAll;
  const templateOk = !selected?.templates || (Boolean(form.templateId) && templateIssues.length === 0 && !mediaBusyKey);
  const valid =
    form.name.trim() &&
    originId &&
    templateOk &&
    (!needsContent || form.text.trim()) &&
    emailOk &&
    voiceOk &&
    senderOk &&
    audienceOk;

  const selectedGroups = useMemo(
    () =>
      (groups || []).filter((group) =>
        (form.idsGrupo || []).some((id) => String(id) === String(group.id))
      ),
    [groups, form.idsGrupo]
  );

  const insertIntoTemplateParam = (token) => {
    const index = Math.min(
      Math.max(0, Number(focusedTemplateParam) || 0),
      Math.max(0, templateParamCount - 1)
    );
    setTemplateParamAt(index, `${templateParamValues[index] || ""}${token}`);
  };

  const messagePanel = selected ? (
    <Panel
      step={2}
      title="Mensagem"
      subtitle={
        selected.templates
          ? "Escolha o template aprovado que será enviado."
          : selected.channelKey === "email"
            ? "Assunto e conteúdo do e-mail."
            : selected.channelKey === "voice"
              ? "Áudio ou texto lido, o que acontece depois e retentativas."
              : "O texto que cada pessoa vai receber."
      }
    >
      <Stack spacing={1.75}>
        {selected.channelKey === "voice" ? (
          <VoiceCampaignMessagePanel
            form={form}
            setForm={setForm}
            voiceFields={voiceFields}
            setVoiceFields={setVoiceFields}
          />
        ) : selected.templates ? (
          <>
            <Autocomplete
              options={templates}
              loading={templatesLoading}
              value={selectedTemplate}
              onChange={(_event, value) => {
                setForm((current) => ({
                  ...current,
                  templateId: value?.id != null ? String(value.id) : "",
                  templateParams: [],
                  templateMedia: {},
                  templateLocation: { latitude: "", longitude: "", name: "", address: "" },
                }));
                setFocusedTemplateParam(0);
                setMediaBusyKey("");
              }}
              getOptionLabel={(option) => templateOptionLabel(option)}
              isOptionEqualToValue={(option, value) =>
                String(option?.id) === String(value?.id)
              }
              filterOptions={(options, state) => {
                const query = String(state.inputValue || "")
                  .trim()
                  .toLowerCase();
                if (!query) return options;
                return options.filter((option) =>
                  templateSearchText(option).includes(query)
                );
              }}
              noOptionsText={
                templatesLoading
                  ? "Carregando templates…"
                  : "Nenhum template aprovado neste canal"
              }
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{
                    alignItems: "flex-start !important",
                    flexDirection: "column",
                    gap: 0.5,
                    py: "10px !important",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {option.elementName || option.name || `#${option.id}`}
                    </Typography>
                    {option.languageCode ? (
                      <Chip size="small" label={option.languageCode} variant="outlined" />
                    ) : null}
                    {option.category ? <Chip size="small" label={option.category} /> : null}
                    {(() => {
                      const optionReq = templateRequirementsOf(option);
                      return (
                        <>
                          {optionReq.bodyParamCount > 0 ? (
                            <Chip
                              size="small"
                              label={`${optionReq.bodyParamCount} campo(s)`}
                              variant="outlined"
                            />
                          ) : null}
                          {optionReq.media.length ? (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={
                                optionReq.media.length === 1
                                  ? optionReq.media[0].label
                                  : `${optionReq.media.length} arquivos`
                              }
                            />
                          ) : null}
                          {optionReq.locationRequired ? (
                            <Chip size="small" label="Localização" variant="outlined" />
                          ) : null}
                        </>
                      );
                    })()}
                  </Stack>
                  {option.preview || option.bodyText ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        whiteSpace: "normal",
                        lineHeight: 1.4,
                      }}
                    >
                      {option.preview || option.bodyText}
                    </Typography>
                  ) : null}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template"
                  required
                  placeholder="Buscar por nome ou texto…"
                  helperText={
                    templatesLoading
                      ? "Carregando…"
                      : `${templates.length} disponível${templates.length === 1 ? "" : "eis"}`
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {templatesLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {selectedTemplate ? (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.75,
                  bgcolor: "grey.50",
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Prévia
                  </Typography>
                  {selectedTemplate.languageCode ? (
                    <Chip size="small" label={selectedTemplate.languageCode} />
                  ) : null}
                  {selectedTemplate.category ? (
                    <Chip size="small" label={selectedTemplate.category} variant="outlined" />
                  ) : null}
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                  {templateBodyPreview ||
                    selectedTemplate.preview ||
                    "Sem texto de corpo neste template."}
                </Typography>
                {templateRequirements.media
                  .filter(
                    (slot) =>
                      slot.kind === "image" && String(form.templateMedia?.[slot.key]?.url || "").trim()
                  )
                  .map((slot) => (
                    <Box
                      key={slot.key}
                      component="img"
                      alt={slot.label || "Imagem do template"}
                      src={form.templateMedia[slot.key].url}
                      sx={{
                        mt: 1.5,
                        maxHeight: 180,
                        maxWidth: "100%",
                        borderRadius: 1,
                        objectFit: "contain",
                      }}
                    />
                  ))}
              </Box>
            ) : null}

            {selectedTemplate &&
            (templateRequirements.media.length ||
              templateRequirements.locationRequired ||
              templateParamCount > 0) ? (
              <Alert severity={templateIssues.length || mediaBusyKey ? "warning" : "success"}>
                {templateIssues.length || mediaBusyKey
                  ? `Para disparar este template, complete: ${
                      mediaBusyKey ? "envio do arquivo" : templateIssues.join(", ")
                    }.`
                  : "Template completo. O envio já pode ser criado."}
              </Alert>
            ) : null}

            {selectedTemplate && templateRequirements.media.length ? (
              <Stack spacing={1.25}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Arquivos do template
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Este template só sai com a mídia anexada. Cada arquivo abaixo é obrigatório.
                </Typography>
                {templateRequirements.media.map((slot) => {
                  const attached = form.templateMedia?.[slot.key];
                  const busy = mediaBusyKey === slot.key;
                  return (
                    <Stack key={slot.key} spacing={0.75}>
                      <Button variant="outlined" component="label" disabled={Boolean(mediaBusyKey)}>
                        {busy ? "Enviando arquivo…" : attached?.url ? `Trocar ${slot.label.toLowerCase()}` : `Anexar ${slot.label.toLowerCase()}`}
                        <input
                          hidden
                          type="file"
                          accept={slot.accept || MEDIA_KIND_META[slot.kind]?.accept || undefined}
                          onChange={(event) => uploadTemplateMedia(slot, event)}
                        />
                      </Button>
                      <Typography
                        variant="caption"
                        color={attached?.url ? "text.secondary" : "warning.main"}
                        sx={{ wordBreak: "break-all" }}
                      >
                        {attached?.filename || attached?.url || `Falta anexar ${slot.label.toLowerCase()}.`}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            ) : null}

            {selectedTemplate && templateRequirements.locationRequired ? (
              <Stack spacing={1.25}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Localização do template
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Informe o ponto que vai no cabeçalho. Latitude e longitude são obrigatórias.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                  <TextField
                    label="Latitude"
                    required
                    fullWidth
                    value={form.templateLocation?.latitude || ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        templateLocation: {
                          ...(current.templateLocation || {}),
                          latitude: event.target.value,
                        },
                      }))
                    }
                  />
                  <TextField
                    label="Longitude"
                    required
                    fullWidth
                    value={form.templateLocation?.longitude || ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        templateLocation: {
                          ...(current.templateLocation || {}),
                          longitude: event.target.value,
                        },
                      }))
                    }
                  />
                </Stack>
                <TextField
                  label="Nome do local"
                  fullWidth
                  value={form.templateLocation?.name || ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      templateLocation: {
                        ...(current.templateLocation || {}),
                        name: event.target.value,
                      },
                    }))
                  }
                />
                <TextField
                  label="Endereço"
                  fullWidth
                  value={form.templateLocation?.address || ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      templateLocation: {
                        ...(current.templateLocation || {}),
                        address: event.target.value,
                      },
                    }))
                  }
                />
              </Stack>
            ) : null}

            {selectedTemplate && templateParamCount > 0 ? (
              <Stack spacing={1.25}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Campos do template
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Todos os campos são obrigatórios. Foque em um e use dados do cadastro se
                  quiser personalizar.
                </Typography>
                {Array.from({ length: templateParamCount }, (_, index) => (
                  <TextField
                    key={`tpl-param-${selectedTemplate.id}-${index}`}
                    label={`Campo {{${index + 1}}}`}
                    value={templateParamValues[index] || ""}
                    onChange={(event) => setTemplateParamAt(index, event.target.value)}
                    onFocus={() => setFocusedTemplateParam(index)}
                    required
                    error={!String(templateParamValues[index] || "").trim()}
                    helperText={
                      String(templateParamValues[index] || "").trim()
                        ? " "
                        : "Obrigatório para este template."
                    }
                    fullWidth
                    placeholder="Texto fixo ou dado do cadastro"
                  />
                ))}
                <CadastroPersonalizePanel
                  fieldHint="no campo em foco"
                  onInsert={insertIntoTemplateParam}
                />
              </Stack>
            ) : null}
          </>
        ) : null}

        {selected.channelKey === "email" ? (
          <>
            <TextField
              label="Assunto"
              value={form.subject}
              onChange={change("subject")}
              onFocus={() => setFocusedEmailField("subject")}
              required
              fullWidth
              placeholder="Ex.: Olá, novidades para você"
            />
            <TextField
              label="Mensagem"
              value={form.text}
              onChange={change("text")}
              onFocus={() => setFocusedEmailField("text")}
              multiline
              minRows={5}
              required
              fullWidth
              InputProps={aiFieldInputProps({
                loading: aiBusy === "text",
                label: "Melhorar mensagem com IA",
                onClick: () => requestAiCopy("text"),
              })}
            />
            <CadastroPersonalizePanel
              fieldHint={
                focusedEmailField === "subject"
                  ? "no assunto"
                  : focusedEmailField === "html"
                    ? "no HTML"
                    : "na mensagem"
              }
              onInsert={(token) => {
                const field =
                  focusedEmailField === "subject"
                    ? "subject"
                    : focusedEmailField === "html"
                      ? "html"
                      : "text";
                setForm((current) => ({
                  ...current,
                  [field]: `${current[field] || ""}${token}`,
                }));
              }}
            />
            <AdvancedPanel
              title="Versão HTML"
              summary="Opcional — se vazio, usamos o texto simples"
            >
              <Stack spacing={1.5}>
                <TextField
                  label="HTML"
                  value={form.html}
                  onChange={change("html")}
                  onFocus={() => setFocusedEmailField("html")}
                  multiline
                  minRows={3}
                  fullWidth
                  InputProps={aiFieldInputProps({
                    loading: aiBusy === "html",
                    label: "Gerar HTML com IA",
                    onClick: () => requestAiCopy("html"),
                  })}
                  helperText="A IA formata o HTML com base no texto da mensagem."
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                  onClick={() => setEmailPreviewOpen(true)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Como o cliente vê
                </Button>
              </Stack>
            </AdvancedPanel>
          </>
        ) : null}

        {needsContent && selected.channelKey !== "email" ? (
          <>
            <TextField
              label="Mensagem"
              value={form.text}
              onChange={change("text")}
              multiline
              minRows={5}
              required
              fullWidth
              InputProps={aiFieldInputProps({
                loading: aiBusy === "text",
                label: "Melhorar mensagem com IA",
                onClick: () => requestAiCopy("text"),
              })}
              helperText={
                smsMeta
                  ? `${smsMeta.chars} caracteres · ${smsMeta.segments} segmento(s)`
                  : selected.channelKey === "sms"
                    ? "Inclua como sair da lista, por exemplo: responda SAIR."
                    : undefined
              }
            />
            <CadastroPersonalizePanel
              onInsert={(token) =>
                setForm((current) => ({
                  ...current,
                  text: `${current.text || ""}${token}`,
                }))
              }
            />
          </>
        ) : null}
      </Stack>
    </Panel>
  ) : null;

  const audiencePanel = selected ? (
    <Panel
      step={3}
      title="Quem recebe"
      subtitle={
        hasFixedAudience
          ? "Seleção feita na tela de Clientes."
          : "Defina o público deste envio."
      }
    >
      <Stack spacing={1.75}>
        {hasFixedAudience ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={700}>
              {fixedClientIds.length} cliente
              {fixedClientIds.length === 1 ? "" : "s"} selecionado
              {fixedClientIds.length === 1 ? "" : "s"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.35 }}>
              Na estimativa, só entram contatos válidos para o canal escolhido.
            </Typography>
          </Alert>
        ) : (
          <>
            <Autocomplete
              multiple
              fullWidth
              disableCloseOnSelect
              options={groups}
              value={selectedGroups}
              onChange={setAudienceGroups}
              getOptionLabel={(option) =>
                String(option?.nome || option?.name || `Grupo #${option?.id || ""}`)
              }
              isOptionEqualToValue={(option, value) =>
                String(option?.id) === String(value?.id)
              }
              noOptionsText="Nenhum nível cadastrado"
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                    label={option.nome || option.name || `#${option.id}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Níveis de consciência"
                  placeholder={
                    selectedGroups.length ? "" : "Opcional — um ou mais níveis"
                  }
                />
              )}
            />

            <AdvancedPanel
              title="Mais filtros"
              summary={
                hasExtraAudienceFilters
                  ? "Filtros extras ativos"
                  : "Tags, UF e cidade"
              }
            >
              <Stack spacing={2}>
                <Autocomplete
                  multiple
                  fullWidth
                  disableCloseOnSelect
                  options={tagOptions}
                  value={Array.isArray(form.tags) ? form.tags : []}
                  onChange={setAudienceTags}
                  noOptionsText={
                    tagOptions.length
                      ? "Nenhuma tag encontrada"
                      : "Nenhuma tag cadastrada ainda"
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        size="small"
                        label={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder={form.tags?.length ? "" : "Selecione"}
                    />
                  )}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControl fullWidth sx={{ maxWidth: { sm: 180 } }}>
                    <InputLabel>UF</InputLabel>
                    <Select label="UF" value={form.uf} onChange={change("uf")}>
                      <MenuItem value="">Todo o Brasil</MenuItem>
                      {BR_UF_OPTIONS.map((uf) => (
                        <MenuItem key={uf.code} value={uf.code}>
                          {uf.code} — {uf.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Cidade"
                    value={form.cidade}
                    onChange={change("cidade")}
                    fullWidth
                    placeholder="Ex.: São Paulo"
                  />
                </Stack>
              </Stack>
            </AdvancedPanel>
          </>
        )}

        {!hasFixedAudience && isAllAudience ? (
          <Alert
            severity="warning"
            action={
              <FormControlLabel
                sx={{ mr: 0, whiteSpace: "nowrap" }}
                control={
                  <Checkbox
                    checked={confirmAll}
                    onChange={(event) => setConfirmAll(event.target.checked)}
                    size="small"
                  />
                }
                label="Entendi"
              />
            }
          >
            Sem filtros, o envio alcança todos os contatos elegíveis deste canal.
          </Alert>
        ) : null}

        {preview ? (
          <Alert severity="success">
            Estimativa:{" "}
            <Typography component="span" fontWeight={700}>
              {preview.count}
            </Typography>{" "}
            contato(s) em {labelChannel(preview.channelKey) || preview.channelKey}.
          </Alert>
        ) : null}
      </Stack>
    </Panel>
  ) : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: selected ? { md: "min(860px, 88vh)" } : { md: 420 },
            maxHeight: "92vh",
          },
        }}
      >
        <DialogTitle sx={{ px: { xs: 2.5, md: 3.5 }, pt: 3, pb: 1.5 }}>
          <Typography component="span" variant="h5" sx={{ fontWeight: 800, display: "block" }}>
            Novo envio
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75, fontWeight: 400 }}>
            {!selected
              ? "Comece pelo canal. Em seguida você define a mensagem e quem recebe."
              : "Canal escolhido. Agora complete a mensagem e o público — o restante é opcional."}
          </Typography>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 2.5, md: 3.5 },
            py: 3,
            bgcolor: "grey.50",
          }}
        >
          <Stack spacing={2.5} key={advancedKey}>
            {error ? (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            ) : null}

            <Panel
              step={1}
              title="Canal"
              subtitle={
                !capabilities.length
                  ? "Nenhum canal de disparo configurado."
                  : selected
                    ? "Remetente e credenciais vêm do canal escolhido em Configurações → Canais."
                    : "Escolha a conta/canal configurado por onde este envio vai sair."
              }
              sx={!selected ? { py: { xs: 3, md: 4 } } : undefined}
            >
              {!capabilities.length ? (
                <Alert severity="warning">
                  Configure WhatsApp, SMS ou e-mail antes de criar um disparo.
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  <Grid container spacing={1.75} justifyContent={!selected ? "center" : "flex-start"}>
                    {capabilities.map((item) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={item.originId}
                        sx={!selected ? { maxWidth: { md: 280 } } : undefined}
                      >
                        <ChannelCard
                          selected={String(originId) === String(item.originId)}
                          name={item.name || labelChannel(item.channelKey) || "Canal"}
                          channelKey={item.channelKey}
                          identity={item.identity}
                          compact={Boolean(selected)}
                          onClick={() => selectChannel(item)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {selected ? (
                    <Alert
                      severity={
                        channelRequiresSenderIdentity(selected.channelKey) &&
                        !String(selected.identity || "").trim()
                          ? "warning"
                          : "info"
                      }
                      sx={{ py: 0.75 }}
                    >
                      {channelRequiresSenderIdentity(selected.channelKey) &&
                      !String(selected.identity || "").trim()
                        ? selected.channelKey === "email"
                          ? "Este canal de e-mail não tem remetente configurado. Ajuste em Configurações → Canais antes de disparar."
                          : selected.channelKey === "voice"
                            ? "Este Telefone Xpro não tem número de saída (From) configurado. Ajuste em Configurações → Canais antes de disparar."
                          : "Este canal SMS não tem número de saída (From) configurado. Ajuste em Configurações → Canais antes de disparar."
                        : `Saída deste envio: ${
                            String(selected.identity || "").trim()
                              ? formatChannelIdentity(selected.channelKey, selected.identity)
                              : `${labelChannel(selected.channelKey) || selected.channelKey} · ${
                                  selected.name || "canal selecionado"
                                }`
                          }. Credenciais e remetente seguem essa configuração.`}
                    </Alert>
                  ) : null}
                </Stack>
              )}
            </Panel>

            {selected ? (
              <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={7}>
                  {messagePanel}
                </Grid>
                <Grid item xs={12} md={5}>
                  {audiencePanel}
                </Grid>
              </Grid>
            ) : null}

            {selected ? (
              <Stack spacing={1.25}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 0.8, px: 0.5 }}
                >
                  Opcional
                </Typography>
                <AdvancedPanel title="Detalhes da solicitação" summary={leadDefaultsSummary}>
                  <Stack spacing={1.5}>
                    <Typography variant="body2" color="text.secondary">
                      Só quando o disparo cria uma solicitação nova. Conversas já abertas no
                      canal não são alteradas.
                    </Typography>
                    {leadDefaultsSchema ? (
                      <DynamicFields
                        compact
                        schema={leadDefaultsSchema}
                        values={leadDefaultsValues}
                        formState={{ condicoes_labels: leadDefaultsLabels }}
                        onChange={(name, value) =>
                          setLeadDefaultsValues((prev) => ({ ...prev, [name]: value }))
                        }
                        onLabel={(name, label) =>
                          setLeadDefaultsLabels((prev) => ({ ...prev, [name]: label }))
                        }
                        onValuesPatch={(patch) =>
                          setLeadDefaultsValues((prev) => ({ ...prev, ...patch }))
                        }
                        onLabelsPatch={(patch) =>
                          setLeadDefaultsLabels((prev) => ({ ...prev, ...patch }))
                        }
                      />
                    ) : (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        Carregando campos…
                      </Alert>
                    )}
                  </Stack>
                </AdvancedPanel>

                <AdvancedPanel
                  title="Agenda, ritmo e rastreio"
                  summary={
                    form.scheduleAt
                      ? `Agendado · ${form.name || "sem nome"}`
                      : "Nome, horário, ritmo, orçamento e etiqueta"
                  }
                >
                  <Stack spacing={2}>
                    <TextField
                      label="Nome do disparo"
                      value={form.name}
                      onChange={(event) => {
                        setNameTouched(true);
                        change("name")(event);
                      }}
                      fullWidth
                      required
                      placeholder="Ex.: Promoção de março"
                    />
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Agendar para"
                      value={form.scheduleAt}
                      onChange={change("scheduleAt")}
                      InputLabelProps={{ shrink: true }}
                      helperText="Horário de Brasília (UTC−3). Com data/hora, o envio dispara sozinho nesse horário. Vazio = rascunho para disparar depois."
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Ritmo (por minuto)"
                        value={form.throttlePerMinute}
                        onChange={change("throttlePerMinute")}
                        inputProps={{ min: 1, max: 600 }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Orçamento do disparo (R$)"
                        value={form.budget}
                        onChange={change("budget")}
                        inputProps={{ min: 0, step: "0.01" }}
                        helperText="Opcional. Valor informado por você para calcular custo por conversa e por venda no Dashboard → Campanhas."
                      />
                    </Stack>
                    <TextField
                      label="Etiqueta de rastreio"
                      value={form.tag}
                      onChange={change("tag")}
                      fullWidth
                      placeholder="Ex.: natal-2026"
                      helperText="Opcional. Ajuda a filtrar resultados depois."
                    />
                  </Stack>
                </AdvancedPanel>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2.5, md: 3.5 },
            py: 2.25,
            gap: 1.25,
            flexWrap: "wrap",
            bgcolor: "background.paper",
          }}
        >
          <Button onClick={onClose} disabled={busy} size="large">
            Cancelar
          </Button>
          <Box sx={{ flex: 1 }} />
          {selected ? (
            <Button
              onClick={previewAudience}
              disabled={busy || !originId}
              variant="outlined"
              size="large"
            >
              {busy ? "Estimando…" : "Estimar público"}
            </Button>
          ) : null}
          <Button
            variant="contained"
            onClick={create}
            disabled={busy || !valid}
            size="large"
            sx={{ minWidth: 148, fontWeight: 700 }}
          >
            {busy ? "Criando…" : "Criar envio"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aiPrompt.open}
        onClose={() => {
          if (!aiBusy) setAiPrompt({ open: false, target: "text", value: "" });
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {aiPrompt.target === "html" ? "Gerar e-mail com IA" : "Gerar mensagem com IA"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Digite o que deseja comunicar. A IA vai criar o texto para você revisar.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="O que você deseja comunicar?"
            value={aiPrompt.value}
            onChange={(event) =>
              setAiPrompt((current) => ({ ...current, value: event.target.value }))
            }
            placeholder="Ex.: Convide o cliente para conhecer a promoção de inverno, válida até sexta-feira."
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={Boolean(aiBusy)}
            onClick={() => setAiPrompt({ open: false, target: "text", value: "" })}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={
              aiBusy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
            }
            disabled={Boolean(aiBusy) || !aiPrompt.value.trim()}
            onClick={() => generateWithAi(aiPrompt.target, aiPrompt.value)}
          >
            {aiBusy ? "Gerando…" : "Gerar com IA"}
          </Button>
        </DialogActions>
      </Dialog>

      <EmailClientPreviewDialog
        open={emailPreviewOpen}
        onClose={() => setEmailPreviewOpen(false)}
        subject={form.subject}
        srcDoc={emailHtmlPreviewDoc}
        usingPlainTextFallback={!String(form.html || "").trim()}
      />
    </>
  );
}
